import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STATUS_META, BRAND } from "@/lib/constants";
import { toast } from "sonner";
import { Phone, MapPin, Clock, ChevronRight, LogOut, RefreshCcw, Users, FileText, Settings, Tablet } from "lucide-react";
import ShareLocationButton from "@/components/ShareLocationButton";
import DriverGarage from "@/components/DriverGarage";
import TripChat from "@/components/TripChat";
import { StaffThemeProvider, StaffThemeToggle } from "@/lib/StaffTheme";
import { linkifyPhones } from "@/lib/linkify";
import VehicleClassBadge from "@/components/VehicleClassBadge";
import { DriverPay } from "@/components/DriverPay";
import { DriverAppBanners } from "@/components/DriverAppBanners";
import { DriverSettings } from "@/components/DriverSettings";

export default function Driver() {
  return (
    <StaffThemeProvider>
      <DriverInner/>
    </StaffThemeProvider>
  );
}

function DriverInner() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [trips, setTrips] = useState([]);
  const [busy, setBusy] = useState(true);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("schedule"); // schedule | history | pay
  const [showSettings, setShowSettings] = useState(false);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/drivers/me/trips");
      setTrips(r.data);
    } catch { setTrips([]); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login", { replace: true }); return; }
    if (user.role !== "driver") { nav("/customer", { replace: true }); return; }
    refresh();
  }, [user, loading, nav, refresh]);

  if (!user || user.role !== "driver") return null;

  const upcoming = trips.filter(t => !["completed", "cancelled"].includes(t.status));
  const past = trips.filter(t => ["completed", "cancelled"].includes(t.status)).reverse(); // most recent first

  return (
    <div data-testid="driver-view" className="bg-black min-h-screen text-[#F5F2EB] pb-24">
      <header className="sticky top-0 z-30 bg-black/95 backdrop-blur border-b border-white/10">
        <div className="px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {user.picture && <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover border border-[#D4AF37]/40" data-testid="driver-header-avatar"/>}
            <div>
              <div className="overline">Chauffeur</div>
              <div className="font-serif text-xl text-[#D4AF37]">{user.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StaffThemeToggle compact/>
            <button data-testid="driver-settings-btn" onClick={() => setShowSettings(s => !s)} className={`w-10 h-10 border flex items-center justify-center ${showSettings ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-[#A1A1AA]"}`}><Settings size={16}/></button>
            <button data-testid="driver-refresh" onClick={refresh} className="w-10 h-10 border border-white/10 flex items-center justify-center text-[#A1A1AA]"><RefreshCcw size={16}/></button>
            <button data-testid="driver-logout" onClick={logout} className="w-10 h-10 border border-white/10 flex items-center justify-center text-[#A1A1AA]"><LogOut size={16}/></button>
          </div>
        </div>
      </header>

      {showSettings && <DriverSettings onClose={() => setShowSettings(false)}/>}

      <div className="pt-4">
        <DriverAppBanners/>
      </div>

      {/* App tabs: Schedule · History · My Pay */}
      <div className="px-5 flex gap-1 border-b border-white/10">
        {[["schedule", "Schedule"], ["history", "History"], ["pay", "My Pay"]].map(([k, l]) => (
          <button
            key={k}
            data-testid={`driver-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-3 text-[10px] uppercase tracking-[0.2em] border-b-2 transition ${tab === k ? "border-[#D4AF37] text-[#D4AF37] font-bold" : "border-transparent text-[#A1A1AA]"}`}
          >{l}</button>
        ))}
        <button
          data-testid="driver-tab-passenger"
          onClick={() => nav("/passenger")}
          className="ml-auto px-4 py-3 text-[10px] uppercase tracking-[0.2em] border-b-2 border-transparent text-[#D4AF37] flex items-center gap-1.5"
        ><Tablet size={12}/> Passenger</button>
      </div>

      {tab === "schedule" && (<>
      <section className="px-5 pt-6 pb-3">
        <AvailabilityToggle/>
        <DriverVehicleCard/>
        <div className="overline mb-2 mt-6">Today&apos;s schedule</div>
        <h1 className="font-serif text-4xl leading-none mb-2">{upcoming.length} {upcoming.length === 1 ? "trip" : "trips"}</h1>
        <p className="text-sm text-[#A1A1AA]">Tap a trip to start, navigate or update status.</p>
      </section>

      {busy && <div className="px-5 text-[#A1A1AA] text-sm">Loading…</div>}

      <section className="px-5 mt-4 space-y-3">
        {upcoming.map(t => <DriverTripCard key={t.booking_id} t={t} onOpen={() => setActive(t)}/>)}
        {!busy && upcoming.length === 0 && (
          <div className="border border-white/10 bg-[#0A0A0A] p-10 text-center">
            <div className="font-serif text-xl text-[#F5F2EB] mb-2">No trips assigned.</div>
            <p className="text-sm text-[#A1A1AA]">Check back soon — dispatch will assign you a trip shortly.</p>
            <p className="text-xs text-[#71717A] mt-4">Need help? Call {BRAND.phones[0]}</p>
          </div>
        )}
      </section>
      </>)}

      {tab === "history" && (
        <section className="px-5 mt-6" data-testid="driver-history-tab">
          <div className="overline mb-1">Trip history</div>
          <h1 className="font-serif text-3xl leading-none mb-4">{past.length} past {past.length === 1 ? "trip" : "trips"}</h1>
          {past.length === 0 && !busy && (
            <div className="border border-white/10 bg-[#0A0A0A] p-10 text-center text-sm text-[#A1A1AA]">
              Completed trips will appear here.
            </div>
          )}
          <div className="space-y-3">
            {past.map(t => (
              <div key={t.booking_id} className="border border-white/5 bg-[#0A0A0A] p-4" data-testid={`history-trip-${t.booking_id}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="overline">{t.confirmation_code}</div>
                    <div className="text-sm mt-0.5">{t.customer_name} · {t.vehicle_name}</div>
                    <div className="text-[11px] text-[#71717a] mt-1">
                      {new Date(t.pickup_datetime).toLocaleString()} · {t.pickup_address}
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="text-xs uppercase tracking-[0.18em]" style={{ color: STATUS_META[t.status]?.color }}>{STATUS_META[t.status]?.label}</div>
                    {t.driver_payout != null && (
                      <div className="text-[11px] mt-1.5 text-[#D4AF37]">${Number(t.driver_payout).toFixed(2)} · {t.driver_payout_status}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "pay" && <DriverPay/>}

      {active && (
        <DriverDetail t={active} onClose={() => setActive(null)} onChange={() => { setActive(null); refresh(); }}/>
      )}
    </div>
  );
}

function DriverTripCard({ t, onOpen }) {
  const meta = STATUS_META[t.status] || STATUS_META.new;
  const actionUrl = t.driver_action_token ? `/driver-trip/${t.driver_action_token}` : null;
  return (
    <div
      data-testid={`driver-trip-${t.booking_id}`}
      className="w-full text-left bg-[#0F0F0F] border border-white/10 p-5 hover:border-[#D4AF37]/60 transition"
    >
      <button onClick={onOpen} className="w-full text-left">
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="overline">{t.confirmation_code}</div>
            <div className="font-serif text-xl mt-1">{t.customer_name}</div>
          </div>
          <span className="status-pill" style={{ color: meta.color }}>{meta.label}</span>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex items-start gap-2 text-[#A1A1AA]"><Clock size={14} className="mt-0.5 text-[#D4AF37]"/>{new Date(t.pickup_datetime).toLocaleString()}</div>
          <div className="flex items-start gap-2 text-[#F5F2EB]"><MapPin size={14} className="mt-0.5 text-[#D4AF37]"/>{t.pickup_address}</div>
          {t.dropoff_address && <div className="flex items-start gap-2 text-[#A1A1AA] pl-5">→ {t.dropoff_address}</div>}
        </div>
      </button>
      <div className="flex justify-between items-center mt-4 pt-3 border-t border-white/5 gap-3">
        <span className="text-xs text-[#A1A1AA] inline-flex items-center gap-1.5">{t.vehicle_name} <VehicleClassBadge vehicleId={t.vehicle_id} vehicleName={t.vehicle_name}/> · {t.pax}<Users size={11} className="inline ml-1"/></span>
        {actionUrl && (
          <a
            href={actionUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`driver-action-link-${t.booking_id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1.5 hover:bg-[#D4AF37] hover:text-black transition"
          >
            Trip Link · Update Status
          </a>
        )}
      </div>
    </div>
  );
}

function DriverDetail({ t, onClose, onChange }) {
  const [note, setNote] = useState("");
  const [flag, setFlag] = useState("fine");
  const [noting, setNoting] = useState(false);

  const setStatus = async (status) => {
    try {
      await api.patch(`/bookings/${t.booking_id}/status`, { status });
      toast.success("Updated");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveNote = async () => {
    if (!note.trim()) { toast.error("Add a note first"); return; }
    setNoting(true);
    try {
      await api.post(`/bookings/${t.booking_id}/driver-note`, { note, flag });
      toast.success("Note saved");
      setNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save note");
    } finally {
      setNoting(false);
    }
  };

  const navUrl = encodeURIComponent(t.pickup_address);
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      <header className="px-5 py-4 border-b border-white/10 flex justify-between items-center">
        <div>
          <div className="overline">{t.confirmation_code}</div>
          <div className="font-serif text-2xl">{t.customer_name}</div>
        </div>
        <button data-testid="driver-detail-close" onClick={onClose} className="text-[#A1A1AA] uppercase text-xs tracking-[0.2em]">Close</button>
      </header>
      <div className="flex-1 overflow-y-auto px-5 py-6 space-y-6">
        {/* Live wait-time card — visible from en_route through in_progress */}
        {["en_route", "on_location", "in_progress"].includes(t.status) && (
          <WaitTimeCard bookingId={t.booking_id}/>
        )}
        <div>
          <div className="overline mb-2">Vehicle</div>
          <div className="font-serif text-xl flex items-center gap-2">
            {t.vehicle_name}
            <VehicleClassBadge vehicleId={t.vehicle_id} vehicleName={t.vehicle_name}/>
          </div>
        </div>
        <div>
          <div className="overline mb-2">Pickup time</div>
          <div className="text-lg">{new Date(t.pickup_datetime).toLocaleString()}</div>
        </div>
        <div>
          <div className="overline mb-2">Pickup</div>
          <div className="text-[#F5F2EB]">{t.pickup_address}</div>
          <a data-testid="navigate-pickup" href={`https://www.google.com/maps/search/?api=1&query=${navUrl}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.22em] mt-2"><MapPin size={14}/>Navigate</a>
        </div>
        {t.dropoff_address && (
          <div>
            <div className="overline mb-2">Drop-off</div>
            <div className="text-[#F5F2EB]">{t.dropoff_address}</div>
          </div>
        )}
        <div>
          <div className="overline mb-2">Passenger</div>
          <div className="text-[#F5F2EB]">{t.customer_name}</div>
          <a data-testid="call-customer" href={`tel:${t.customer_phone}`} className="inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.22em] mt-2"><Phone size={14}/>{t.customer_phone}</a>
        </div>
        {t.notes && (
          <div>
            <div className="overline mb-2">Notes</div>
            <div className="text-sm text-[#A1A1AA] italic">{linkifyPhones(t.notes, "text-[#D4AF37] font-medium underline underline-offset-2")}</div>
          </div>
        )}
        {["assigned", "en_route", "in_progress"].includes(t.status) && (
          <ShareLocationButton booking={t}/>
        )}
        {["assigned", "en_route", "in_progress"].includes(t.status) && (
          <TripChat bookingId={t.booking_id} viewerHint="You · Chauffeur"/>
        )}
        <div className="border-t border-white/10 pt-5">
          <div className="overline mb-2">Payout status</div>
          {t.driver_payout_status === "paid" ? (
            <>
              <div className="font-serif text-2xl text-[#10B981]">Paid</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">
                {t.driver_payout_date || ""} · {(t.driver_payout_method || "").toUpperCase()}
              </div>
            </>
          ) : (
            <>
              <div className="font-serif text-2xl text-[#A1A1AA]">Pending settlement</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">Dispatch will reconcile after the trip</div>
            </>
          )}
        </div>
      </div>
      <div className="border-t border-white/10 p-4 bg-[#0A0A0A] space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Note about this rider (internal)</div>
        <textarea
          data-testid="driver-note-input"
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={2}
          placeholder="e.g. left a bag, polite, no-show, etc."
          className="w-full bg-transparent border border-white/10 px-2 py-2 text-sm focus:border-[#D4AF37] outline-none"
        />
        <div className="flex gap-2">
          {["fine","warning","blocked"].map(f => (
            <button
              key={f}
              type="button"
              data-testid={`driver-flag-${f}`}
              onClick={() => setFlag(f)}
              className={`px-3 py-1 text-[10px] uppercase tracking-[0.18em] border transition-colors ${flag===f ? (f==="blocked" ? "bg-red-700 text-white border-red-700" : f==="warning" ? "bg-yellow-600 text-black border-yellow-600" : "bg-[#D4AF37] text-black border-[#D4AF37]") : "border-white/10 text-[#A1A1AA]"}`}
            >{f}</button>
          ))}
          <button
            type="button"
            data-testid="driver-save-note"
            onClick={saveNote}
            disabled={noting}
            className="ml-auto text-xs uppercase tracking-[0.18em] text-[#D4AF37] disabled:opacity-50"
          >{noting ? "Saving…" : "Save note"}</button>
        </div>
      </div>

      <div className="border-t border-white/10 p-4 flex gap-3 bg-[#0A0A0A]">
        <button
          data-testid="driver-manifest"
          onClick={async () => {
            try {
              const r = await api.get(`/bookings/${t.confirmation_code}/manifest.pdf`, { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
              window.open(url, "_blank");
            } catch (e) { toast.error("Manifest unavailable"); }
          }}
          className="btn-ghost text-[10px] !py-2"
          title="Trip Manifest — show law enforcement"
        ><FileText size={12}/> Manifest</button>
        {t.status === "assigned" && (
          <button data-testid="driver-start-trip" onClick={() => setStatus("en_route")} className="btn-gold flex-1">Start · En route</button>
        )}
        {t.status === "en_route" && (
          <button data-testid="driver-arrived" onClick={async () => {
            try {
              const r = await api.post(`/bookings/${t.booking_id}/passenger-aboard`, {});
              if (r.data?.wait?.status === "billable") {
                toast.success("Passenger aboard — wait time recorded");
              } else {
                toast.success("Passenger aboard");
              }
              if (onChange) onChange();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Failed");
            }
          }} className="btn-gold flex-1">Customer Aboard · Start trip</button>
        )}
        {t.status === "in_progress" && (
          <button data-testid="driver-complete" onClick={() => setStatus("completed")} className="btn-gold flex-1">Complete</button>
        )}
        {!["assigned", "en_route", "in_progress"].includes(t.status) && (
          <button data-testid="driver-noop" onClick={onClose} className="btn-ghost flex-1">Done</button>
        )}
      </div>
    </div>
  );
}


function AvailabilityToggle() {
  const [available, setAvailable] = React.useState(false);
  const [setAt, setSetAt] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/drivers/me/availability");
        setAvailable(!!r.data.available_now);
        setSetAt(r.data.set_at || null);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const toggle = async () => {
    const next = !available;
    setBusy(true);
    try {
      const r = await api.patch("/drivers/me/availability", { available_now: next });
      setAvailable(r.data.available_now);
      setSetAt(r.data.set_at);
      toast.success(next ? "You're marked AVAILABLE NOW · dispatch can assign you next" : "You're now offline for auto-assign");
    } catch (e) { toast.error("Could not update"); }
    finally { setBusy(false); }
  };

  if (loading) return null;

  const sinceText = setAt ? `Since ${new Date(setAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : "";

  return (
    <div
      data-testid="driver-availability"
      className={`border ${available ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 bg-[#0A0A0A]"} p-4 flex items-center justify-between`}
    >
      <div className="flex items-center gap-3">
        <span className={`w-3 h-3 rounded-full ${available ? "bg-emerald-400 animate-pulse" : "bg-zinc-600"}`}/>
        <div>
          <div className={`font-serif text-lg ${available ? "text-emerald-300" : "text-[#F5F2EB]"}`}>
            {available ? "Available now" : "Tap to go available"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] mt-0.5">
            {available ? `Eligible for one-click assign · ${sinceText}` : "Dispatch can auto-assign you next when you're ready"}
          </div>
        </div>
      </div>
      <button
        data-testid="driver-availability-toggle"
        onClick={toggle}
        disabled={busy}
        className={`px-5 py-2 text-xs uppercase tracking-[0.2em] font-medium ${
          available
            ? "bg-emerald-500/30 text-emerald-200 border border-emerald-500/40"
            : "bg-[#D4AF37] text-[#0a0a0a]"
        } disabled:opacity-60`}
      >
        {busy ? "…" : available ? "Go offline" : "I'm available"}
      </button>
    </div>
  );
}



function WaitTimeCard({ bookingId }) {
  const [s, setS] = useState(null);

  useEffect(() => {
    let live = true;
    const poll = async () => {
      try {
        const r = await api.get(`/bookings/${bookingId}/wait-status`);
        if (live) setS(r.data);
      } catch {/* ignore */}
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => { live = false; clearInterval(t); };
  }, [bookingId]);

  if (!s) return null;
  const isLocked = s.locked;
  const classLabel = {
    airport: "Airport · 60 min free",
    amtrak: "Amtrak / station · 30 min free",
    local: "Local pickup · 15 min free",
  }[s.pickup_classification] || "Local pickup";

  let mainColor = "#10B981"; // green = within free
  let mainLabel = "Within free wait";
  let mainValue = "";
  if (s.status === "before_scheduled") {
    mainColor = "#A1A1AA";
    mainLabel = "Pickup not yet scheduled";
    const minsEarly = Math.abs(s.elapsed_min_after_scheduled);
    mainValue = `${minsEarly.toFixed(0)} min early`;
  } else if (s.status === "within_free") {
    mainColor = "#10B981";
    mainLabel = isLocked ? "Locked · within free" : "Free wait remaining";
    mainValue = `${s.free_remaining_min.toFixed(0)} min`;
  } else if (s.status === "billable") {
    mainColor = "#F59E0B";
    mainLabel = isLocked ? "Wait time recorded" : "Over free wait";
    mainValue = `${s.billable_minutes} min over`;
  }

  return (
    <div className="border p-4" style={{ borderColor: `${mainColor}55`, background: `${mainColor}10` }} data-testid="wait-time-card">
      <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.22em]" style={{ color: mainColor }}>
        <Clock size={13}/> Pickup wait timer
        {isLocked && <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-[#71717A]">Locked</span>}
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] mb-2">{classLabel}</div>
      <div className="flex items-end gap-4">
        <div>
          <div className="font-serif text-4xl" style={{ color: mainColor }}>{mainValue || "—"}</div>
          <div className="text-[11px] text-[#A1A1AA] mt-1">{mainLabel}</div>
        </div>
        <div className="ml-auto text-right text-[11px] text-[#A1A1AA]">
          {s.status === "billable" && (
            <div className="text-[10px] uppercase tracking-[0.18em]">Customer will be billed automatically</div>
          )}
          {s.status === "within_free" && (
            <div className="text-[10px] uppercase tracking-[0.18em]">{s.free_minutes} min free</div>
          )}
        </div>
      </div>
    </div>
  );
}



/* ───────────────────────────────────────────────────────────────────────────
   DRIVER VEHICLE CARD · driver self-edits their vehicle, license & insurance
   ─────────────────────────────────────────────────────────────────────────── */
function DriverVehicleCard() {
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(true);
  const [f, setF] = useState({
    vehicle_year: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_color: "",
    license_plate: "",
    license_no: "",
    license_expiry: "",
    insurance_provider: "",
    insurance_policy_no: "",
    insurance_expiry: "",
  });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/drivers/me/profile");
      setProfile(r.data);
      setF({
        vehicle_year: r.data.vehicle_year || "",
        vehicle_make: r.data.vehicle_make || "",
        vehicle_model: r.data.vehicle_model || "",
        vehicle_color: r.data.vehicle_color || "",
        license_plate: r.data.license_plate || "",
        license_no: r.data.license_no || "",
        license_expiry: r.data.license_expiry || "",
        insurance_provider: r.data.insurance_provider || "",
        insurance_policy_no: r.data.insurance_policy_no || "",
        insurance_expiry: r.data.insurance_expiry || "",
      });
    } catch (e) {
      setProfile(null);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!profile?.driver_id) return;
    setSaving(true);
    try {
      await api.patch(`/drivers/${profile.driver_id}`, f);
      toast.success("Vehicle details saved");
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  if (busy) return null;
  if (!profile) return null;

  const today = new Date().toISOString().slice(0, 10);
  const licExpired = profile.license_expiry && profile.license_expiry < today;
  const insExpired = profile.insurance_expiry && profile.insurance_expiry < today;
  const licSoon = !licExpired && profile.license_expiry && profile.license_expiry < new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const insSoon = !insExpired && profile.insurance_expiry && profile.insurance_expiry < new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

  const inp = (key, ph, type = "text") => (
    <input
      data-testid={`driver-vehicle-${key}`}
      type={type}
      value={f[key]}
      placeholder={ph}
      onChange={e => setF({ ...f, [key]: e.target.value })}
      className="w-full bg-black border border-white/10 px-3 py-2 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none"
    />
  );

  if (!editing) {
    return (
      <div data-testid="driver-vehicle-card" className="mt-4 border border-white/10 bg-[#0A0A0A] p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="overline">My vehicles &amp; credentials</div>
          <button data-testid="driver-vehicle-edit" onClick={() => setEditing(true)}
            className="px-3 py-1 text-[10px] uppercase tracking-[0.2em] border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10">
            Credentials
          </button>
        </div>
        {/* Garage — add every car you drive; the primary one is used when dispatch assigns you */}
        <DriverGarage driverId={profile.driver_id}/>
        <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
          <div className={`border ${licExpired ? "border-red-500/50 bg-red-500/10" : licSoon ? "border-amber-500/40 bg-amber-500/10" : "border-white/5"} p-2`}>
            <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A]">License expiry</div>
            <div className={licExpired ? "text-red-300" : licSoon ? "text-amber-300" : "text-[#F5F2EB]"}>
              {profile.license_expiry || "—"}
            </div>
          </div>
          <div className={`border ${insExpired ? "border-red-500/50 bg-red-500/10" : insSoon ? "border-amber-500/40 bg-amber-500/10" : "border-white/5"} p-2`}>
            <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A]">Insurance expiry</div>
            <div className={insExpired ? "text-red-300" : insSoon ? "text-amber-300" : "text-[#F5F2EB]"}>
              {profile.insurance_expiry || "—"}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="driver-vehicle-card-editing" className="mt-4 border border-[#D4AF37]/40 bg-[#0A0A0A] p-4">
      <div className="overline mb-3">Update credentials</div>

      <div className="overline mt-1 mb-2">Driver licence</div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        {inp("license_no", "DL #")}
        <label className="block">
          <span className="block text-[9px] uppercase tracking-[0.18em] text-[#71717A] mb-1">Expires</span>
          {inp("license_expiry", "", "date")}
        </label>
      </div>

      <div className="overline mt-4 mb-2">Insurance</div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        {inp("insurance_provider", "Provider")}
        {inp("insurance_policy_no", "Policy #")}
      </div>
      <label className="block mb-3">
        <span className="block text-[9px] uppercase tracking-[0.18em] text-[#71717A] mb-1">Insurance expires</span>
        {inp("insurance_expiry", "", "date")}
      </label>

      <div className="flex gap-2 mt-4">
        <button data-testid="driver-vehicle-save" onClick={save} disabled={saving}
          className="flex-1 bg-[#D4AF37] text-black py-2.5 text-xs uppercase tracking-[0.22em] font-medium disabled:opacity-50">
          {saving ? "Saving…" : "Save"}
        </button>
        <button data-testid="driver-vehicle-cancel" onClick={() => setEditing(false)}
          className="flex-1 border border-white/10 text-[#A1A1AA] py-2.5 text-xs uppercase tracking-[0.22em]">
          Cancel
        </button>
      </div>
    </div>
  );
}
